import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Alert,
  CircularProgress,
  Grid,
} from "@mui/material";
import { fetchData } from "../../../api/client";
import { useWorkspaceConfig } from "../../../context/WorkspaceConfigContext";

const LABEL_FIELDS = [
  {
    key: "nomeproduto",
    title: "Campo produto (solicitação)",
    hint: "Obrigatório para exibir o campo. Ex.: Segmento. Vazio = oculto no atendimento.",
  },
  {
    key: "nomecampanha",
    title: "Campo campanha (solicitação)",
    hint: "Obrigatório para exibir o campo. Ex.: Trechos. Vazio = oculto no atendimento.",
  },
  {
    key: "dataadicionallead",
    title: "Data adicional (solicitação)",
    hint: "Vazio oculta o campo. Ex.: Data do ocorrido",
  },
  {
    key: "textoadicionallead",
    title: "Texto adicional (solicitação)",
    hint: "Vazio oculta o campo. Ex.: Trecho",
  },
  {
    key: "campoadicional1simnaocliente",
    title: "Cliente — campo adicional 1",
    hint: "Sim/Não. Vazio oculta.",
  },
  {
    key: "campoadicional2simnaocliente",
    title: "Cliente — campo adicional 2",
    hint: "Sim/Não. Vazio oculta.",
  },
  {
    key: "campoadicional3multiselectcliente",
    title: "Cliente — campo adicional 3",
    hint: "Tags livres. Vazio oculta.",
  },
  { key: "produtospadrao", title: "Lista padrão de produtos", hint: "Valores separados por ;" },
  { key: "campanhaspadrao", title: "Lista padrão de campanhas", hint: "Valores separados por ;" },
];

const INTEGRATION_CONFIG_FIELDS = [
  {
    key: "urlbasebtnidinterno",
    title: "URL base para o botão id interno",
    hint: "Ex.: https://exemplo.com/cadastro?id= — concatenada ao id_interno do cliente. Vazio oculta o botão Cadastro externo.",
  },
];

const FieldLabelsSettings = () => {
  const { refetch: refetchWorkspaceConfig } = useWorkspaceConfig();
  const [labels, setLabels] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const keys = [...LABEL_FIELDS, ...INTEGRATION_CONFIG_FIELDS].map((f) => f.key).join(",");
        const res = await fetchData(`/configurations?keys=${encodeURIComponent(keys)}`, "GET");
        const items = res?.data?.items || res?.items || [];
        const map = {};
        items.forEach((i) => {
          map[i.key] = i.value ?? "";
        });
        if (!cancelled) setLabels(map);
      } catch (e) {
        if (!cancelled) setError(e?.message || "Erro ao carregar labels.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setMessage(null);
      await fetchData("/workspace/field-labels", "PUT", { labels });
      const integrationLabels = {};
      for (const f of INTEGRATION_CONFIG_FIELDS) {
        if (Object.prototype.hasOwnProperty.call(labels, f.key)) {
          integrationLabels[f.key] = labels[f.key];
        }
      }
      await Promise.all(
        Object.entries(integrationLabels).map(([key, value]) =>
          fetchData(`/configurations/${encodeURIComponent(key)}`, "PUT", {
            value: value == null ? "" : String(value),
          })
        )
      );
      await refetchWorkspaceConfig?.();
      setMessage("Labels salvos. Compatível com o Xpro legado (tabela configuracoes).");
    } catch (e) {
      setError(e?.message || "Erro ao salvar.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Labels dos campos adicionais
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Os rótulos configurados aqui são os mesmos do Xpro antigo (Segmento, Trechos, etc.).
        Solicitações e clientes existentes continuam usando as colunas originais no banco.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {message && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {message}
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Grid container spacing={2}>
          {LABEL_FIELDS.map((f) => (
            <Grid item xs={12} md={6} key={f.key}>
              <TextField
                fullWidth
                label={f.title}
                helperText={f.hint}
                value={labels[f.key] ?? ""}
                onChange={(e) =>
                  setLabels((prev) => ({ ...prev, [f.key]: e.target.value }))
                }
              />
            </Grid>
          ))}
        </Grid>
        <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
          Integração de cadastro externo
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Quando preenchida e o cliente tiver id interno, exibe o botão &quot;Cadastro externo&quot; no chat e na ficha do cliente.
        </Typography>
        <Grid container spacing={2}>
          {INTEGRATION_CONFIG_FIELDS.map((f) => (
            <Grid item xs={12} key={f.key}>
              <TextField
                fullWidth
                label={f.title}
                helperText={f.hint}
                value={labels[f.key] ?? ""}
                onChange={(e) =>
                  setLabels((prev) => ({ ...prev, [f.key]: e.target.value }))
                }
              />
            </Grid>
          ))}
        </Grid>
        <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? "Salvando…" : "Salvar labels"}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default FieldLabelsSettings;
